/**
 * AnnotationMarker
 * Minimaler 2D-Marker auf der Punktwolke.
 *
 * Darstellung:
 *  • Kleiner farbiger Punkt am Koordinatenpunkt
 *  • Titel als permanentes Label neben dem Punkt
 *  • Klick öffnet ein kompaktes Popup im hellen Card-Stil:
 *    – Koordinaten-Header (Position + Kamera)
 *    – Typ-Label (bold) + Titel
 *    – Short Info (wenn vorhanden)
 *    – Tags (wenn vorhanden)
 *    – Bearbeiten-Button
 *  • Zweiter Klick oder Klick außerhalb schließt das Popup
 */

import React, { useRef, useEffect, useState } from 'react'
import {
  Annotation,
  ANNOTATION_TYPE_COLORS,
  ANNOTATION_TYPE_LABELS,
} from '../../types/annotations'
import { useAnnotationStore } from '../../store/annotations'
import { useCameraNavigation } from '../../hooks/useCameraNavigation'
import { C } from '../../styles/theme'
import type { ZoomTier, AnnotationConfig, AnnotationPopupConfig } from '../../hooks/useSemanticZoom'
import {
  PaperlessDocument,
  fetchDocuments,
  getDocumentThumbnailUrl,
} from '../../lib/paperless/api'

// ─── Small SVG icon components ───────────────────────────────────────────────

function IconEdit() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
    </svg>
  )
}
function IconMove() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/>
      <polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/>
      <line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/>
    </svg>
  )
}
function IconCopy() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  )
}
function IconTrash() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  )
}

// ─── Marker-Größe (LOD-Defaults, in px — Basis für Canvas-Positionsberechnung) ──

const DOT_SIZE    = 12    // px: sichtbarer Marker-Punkt (wird per Tier überschrieben)
const HIT_SIZE    = 24    // px: Hover-/Klickfläche (unsichtbar, größer)

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtVec3(v: { x: number; y: number; z: number }) {
  return `(${v.x.toFixed(1)}, ${v.y.toFixed(1)}, ${v.z.toFixed(1)})`
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface AnnotationMarkerProps {
  annotation: Annotation
  viewerRef: React.RefObject<any>
  /** Semantic zoom tier – controls dot size, label visibility, popup behavior */
  tier?: ZoomTier
  /** Annotation layer config from semantic zoom */
  tierConfig?: AnnotationConfig
  /** Popup config from semantic zoom */
  popupConfig?: AnnotationPopupConfig
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AnnotationMarker({ annotation, viewerRef, tier, tierConfig, popupConfig }: AnnotationMarkerProps) {
  const popupRef = useRef<HTMLDivElement>(null)

  const openAnnotationEditor = useAnnotationStore(s => s.openAnnotationEditor)
  const deleteAnnotation = useAnnotationStore(s => s.deleteAnnotation)
  const duplicateAnnotation = useAnnotationStore(s => s.duplicateAnnotation)
  const startRelocating = useAnnotationStore(s => s.startRelocatingAnnotation)
  const setHovered = useAnnotationStore(s => s.setHoveredAnnotation)
  const selectedAnnotationId = useAnnotationStore(s => s.selectedAnnotationId)
  const setSelectedAnnotation = useAnnotationStore(s => s.setSelectedAnnotation)
  const { flyToCamera } = useCameraNavigation({ viewerRef })

  const isOpen = selectedAnnotationId === annotation.id
  const setIsOpen = (open: boolean) => setSelectedAnnotation(open ? annotation.id : null)

  // LOD: tier-abhängige Konfiguration
  const activeTier = tier ?? 'detail'
  const dotSize = tierConfig?.dotSize ?? DOT_SIZE
  const showLabel = tierConfig?.label !== false
  const popupMode = tierConfig?.popup ?? 'click'
  const showPopupTags = popupConfig?.showTags ?? true
  const popupVariant = popupConfig?.variant ?? 'expanded'

  const color = ANNOTATION_TYPE_COLORS[annotation.type] ?? '#999'
  const typeLabel = ANNOTATION_TYPE_LABELS[annotation.type] ?? annotation.type

  // Linked Paperless docs
  const docIds = annotation.paperlessDocIds ?? []
  const hasLinkedDocs = docIds.length > 0
  const [linkedDocs, setLinkedDocs] = useState<PaperlessDocument[]>([])
  useEffect(() => {
    if (!isOpen || docIds.length === 0) { setLinkedDocs([]); return }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetchDocuments({ page_size: 50 })
        if (cancelled) return
        const byId = new Map(res.results.map(d => [d.id, d]))
        setLinkedDocs(docIds.map(id => byId.get(id)).filter(Boolean) as PaperlessDocument[])
      } catch { /* ignore */ }
    })()
    return () => { cancelled = true }
  }, [isOpen, docIds.join(',')])

  // dotSizeEm: dot-Größe in em (relativ zu 16px-Basis)
  const dotSizeEm = `${dotSize / 16}em`

  // Close popup on outside click
  useEffect(() => {
    if (!isOpen) return
    function handleOutside(e: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', handleOutside), 50)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleOutside)
    }
  }, [isOpen])

  function handleMouseEnter() {
    setHovered(annotation.id)
  }
  function handleMouseLeave() {
    setHovered(null)
  }

  const canShowPopup = annotation.showPopup !== false

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation()
    ;(window as any).resetClipBoxToBounds?.()
    flyToCamera(annotation.camera)
    // Click opens popup — stays open until X or outside click
    if (canShowPopup) {
      setIsOpen(!isOpen)
    }
  }

  const hasTooltip = !!annotation.tooltip?.trim()
  const hasTags = annotation.tags.length > 0

  return (
    <div
      data-component="AnnotationMarker"
      data-annotation-type={annotation.type}
      ref={popupRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        zIndex: isOpen ? 10200 : 10100,
        userSelect: 'none',
      }}
    >
      {/* ── Marker-Punkt + permanentes Label ─────────────────────── */}
      <div
        data-element="marker-dot"
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.375em',   /* 6px at 16px */
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {/* Dot — SVG circle + ring, Größe tier-abhängig (in em) */}
        <div
          data-element="dot-circle"
          style={{
            width: dotSizeEm,
            height: dotSizeEm,
            flexShrink: 0,
            transition: `all ${C.transition}, opacity 0.3s ease, width 0.2s ease, height 0.2s ease`,
            transform: isOpen ? 'scale(1.3)' : 'scale(1)',
          }}
        >
          <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" fill="#ffffff" stroke={color} strokeWidth="2.5" />
            <circle cx="12" cy="12" r="4" fill={color} />
          </svg>
        </div>
        {/* Name — tier-abhängig sichtbar */}
        {showLabel && (
          <div
            data-section="marker-label"
            style={{
              padding: '0.1818em 0.6364em', /* 2px/11px, 7px/11px */
              borderRadius: C.radiusSm,
              background: 'rgba(0,0,0,0.72)',
              color: '#fff',
              fontSize: '0.6875em',         /* 11px at 16px */
              fontWeight: 600,
              fontFamily: C.fontSans,
              pointerEvents: 'auto',
              boxShadow: '0 0.0909em 0.3636em rgba(0,0,0,0.3)', /* 1px/11px, 4px/11px */
              maxWidth: '12.5em',           /* 200px at 16px */
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.4545em',       /* 16px/11px */
              transition: 'opacity 0.2s ease',
            }}
          >
            {annotation.title}
          </div>
        )}
      </div>

      {/* ── Popup: helles Card-Design (LOD-aware) ────────────────── */}
      {isOpen && canShowPopup && (
        <div
          data-section="marker-popup"
          data-popup-variant={popupVariant}
          style={{
            position: 'absolute',
            /* bottom offset: (dotSize/2 + 24) / 16 em — more spacing from marker */
            bottom: `${(dotSize / 2 + 24) / 16}em`,
            left: '50%',
            transform: 'translateX(-50%)',
            width: popupVariant === 'compact' ? '15em' : '18.75em', /* 240px / 300px at 16px */
            background: '#ffffff',
            border: `1px solid ${C.border}`,
            borderRadius: '0.625em',  /* 10px at 16px */
            boxShadow: '0 0.5em 1.75em rgba(0, 0, 0, 0.12), 0 0.125em 0.5em rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
            fontFamily: C.fontSans,
            transition: 'width 0.2s ease',
          }}
        >
          {/* ── Type Header ────────────────────────────────────────── */}
          <div
            data-section="popup-type-header"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.5em 0.75em',
              gap: '0.375em',
              background: '#fafafa',
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '1.125em', height: '1.125em', flexShrink: 0,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" fill="#ffffff" stroke={color} strokeWidth="2.5" />
                <circle cx="12" cy="12" r="4" fill={color} />
              </svg>
            </span>
            <span style={{
              fontSize: '0.625em',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: color,
              flex: 1,
            }}>
              {typeLabel}
            </span>
            {/* Close button */}
            <button
              onClick={(e) => { e.stopPropagation(); setIsOpen(false) }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '1.25em', height: '1.25em',
                border: 'none', background: 'transparent',
                color: '#9ca3af', cursor: 'pointer',
                borderRadius: '0.25em', padding: 0, flexShrink: 0,
                fontSize: '1em',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = C.hoverBg; e.currentTarget.style.color = C.textPrimary }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af' }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/>
              </svg>
            </button>
          </div>

          {/* ── Body ───────────────────────────────────────────────── */}
          <div style={{ padding: '0.75em 0.875em' }}>

            {/* Titel */}
            <div style={{
              fontSize: '0.875em',   /* 14px at 16px */
              fontWeight: 700,
              color: C.textPrimary,
              marginBottom: hasTooltip ? '0.5em' : '0.25em', /* 8px/14px, 4px/14px */
              lineHeight: 1.3,
              letterSpacing: '-0.01em',
            }}>
              {annotation.title}
            </div>

            {/* Short Info / Tooltip */}
            {hasTooltip && (
              <div style={{
                fontSize: '0.75em',   /* 12px at 16px */
                lineHeight: 1.5,
                color: C.textMuted,
                marginBottom: hasTags ? '0.8333em' : '0.3333em', /* 10px/12px, 4px/12px */
              }}>
                {annotation.tooltip}
              </div>
            )}

            {/* Tags (nur bei showPopupTags) */}
            {hasTags && showPopupTags && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.25em',     /* 4px at 16px */
                marginBottom: '0.25em',
              }}>
                {annotation.tags.map(tag => (
                  <span key={tag} style={{
                    fontSize: '0.5625em',           /* 9px at 16px */
                    padding: '0.2222em 0.6667em',   /* 2px/9px, 6px/9px */
                    borderRadius: '0.3333em',        /* 3px/9px */
                    background: C.pillBg,
                    color: C.textDim,
                    fontFamily: C.fontSans,
                    border: `1px solid ${C.borderSubtle}`,
                  }}>
                    #{tag}
                  </span>
                ))}
              </div>
            )}
            {/* Linked Paperless Docs */}
            {hasLinkedDocs && linkedDocs.length > 0 && (
              <div style={{ marginTop: '0.5em' }}>
                {linkedDocs.map(doc => (
                  <div
                    key={doc.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      if ((window as any).showDocViewer) {
                        (window as any).showDocViewer({ id: doc.id, title: doc.title, fileName: doc.original_file_name, tags: [], created: doc.created, pageCount: null, docTypeName: null })
                      }
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.375em',
                      padding: '0.3125em 0.4375em',
                      borderRadius: '0.25em',
                      border: `1px solid ${C.border}`,
                      background: '#fafafa',
                      marginBottom: '0.25em',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff' }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fafafa' }}
                  >
                    <img
                      src={getDocumentThumbnailUrl(doc.id)}
                      alt=""
                      style={{
                        width: '1.5em', height: '1.5em',
                        borderRadius: '0.125em',
                        objectFit: 'cover',
                        background: '#e5e7eb',
                        flexShrink: 0,
                      }}
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                    <span style={{
                      fontSize: '0.6875em', fontWeight: 500, color: C.textPrimary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      flex: 1,
                    }}>
                      {doc.title}
                    </span>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={C.textDim} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Footer: Aktions-Buttons ────────────────────────────── */}
          <div
            data-section="popup-footer"
            style={{
              padding: '0.375em 0.625em 0.5em', /* 6px/16px, 10px/16px, 8px/16px */
              borderTop: `1px solid ${C.border}`,
              display: 'flex',
              gap: '0.25em',  /* 4px at 16px */
            }}
          >
            <PopupAction
              icon={<IconEdit />}
              label="Bearbeiten"
              iconOnly
              onClick={(e) => {
                e.stopPropagation()
                openAnnotationEditor(annotation.id)
                setIsOpen(false)
              }}
            />
            <PopupAction
              icon={<IconMove />}
              label="Verschieben"
              iconOnly
              onClick={(e) => {
                e.stopPropagation()
                startRelocating(annotation.id)
                setIsOpen(false)
              }}
            />
            <PopupAction
              icon={<IconCopy />}
              label="Duplizieren"
              iconOnly
              onClick={(e) => {
                e.stopPropagation()
                const dup = duplicateAnnotation(annotation.id)
                if (dup) openAnnotationEditor(dup.id)
                setIsOpen(false)
              }}
            />
            <div style={{ flex: 1 }} />
            <PopupAction
              icon={<IconTrash />}
              label=""
              danger
              iconOnly
              onClick={(e) => {
                e.stopPropagation()
                deleteAnnotation(annotation.id)
                setIsOpen(false)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Sub-component: Popup-Action-Button ──────────────────────────────────────

function PopupAction({
  icon,
  label,
  danger,
  iconOnly,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  danger?: boolean
  iconOnly?: boolean
  onClick: (e: React.MouseEvent) => void
}) {
  return (
    <button
      data-element={`popup-action-${(label || 'delete').toLowerCase()}`}
      onClick={onClick}
      title={label || (danger ? 'Löschen' : '')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: iconOnly ? 'center' : undefined,
        gap: iconOnly ? '0' : '0.25em',          /* 4px at 16px */
        padding: iconOnly ? '0.25em' : '0.25em 0.5em', /* 4px/16px, 8px/16px */
        borderRadius: '0.3125em',                /* 5px at 16px */
        border: 'none',
        background: 'transparent',
        color: danger ? C.danger : C.textMuted,
        fontSize: '0.625em',                     /* 10px at 16px */
        fontWeight: 500,
        cursor: 'pointer',
        fontFamily: C.fontSans,
        transition: `all 0.15s ease`,
        whiteSpace: 'nowrap',
        width: iconOnly ? '1.625em' : undefined,  /* 26px/16px */
        height: iconOnly ? '1.625em' : undefined,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = danger ? C.dangerLight : C.hoverBg
        e.currentTarget.style.color = danger ? C.danger : C.textPrimary
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent'
        e.currentTarget.style.color = danger ? C.danger : C.textMuted
      }}
    >
      {icon}
      {!iconOnly && label}
    </button>
  )
}
